import { useState } from "react";
import { useQuery } from "../lib/dataClient.js";
import { stepIds } from "../lib/dimensionDefs.js";
import EmptyState from "./EmptyState.jsx";
import GridStatus from "./GridStatus.jsx";
import UnavailableLinkPage from "./UnavailableLinkPage.jsx";
import { openCurrentOrganizationHome } from "../lib/tenantLink.js";

const ACTION_CLASS = "min-h-[28px] rounded-[2px] border border-[color:var(--line)] bg-transparent px-[12px] text-[12px] leading-[1.18] text-[color:var(--muted)] transition-colors hover:border-[color:var(--line-strong)] hover:text-[color:var(--ink)]";

function EndpointLookup({ endpointIds, children, focusHeader, onBack, onRetry }) {
  const { data, loading, error, denied } = useQuery(
    "getEndpointScopeStatus", { endpointIds }, { staleMs: 0 },
  );
  const unavailable = denied || error?.status === 403;
  const pending = !unavailable && !error && (loading || data == null);
  if (!pending && !error && !denied && data?.found) return children;
  if (unavailable || (!pending && !error && !data?.found)) {
    return <UnavailableLinkPage onOpenDashboard={openCurrentOrganizationHome} />;
  }

  const title = error ? "Couldn't load endpoint" : "Loading endpoint";
  const message = error ? "We couldn't check this endpoint. Try again." : null;

  return (
    <section className="flex h-full min-h-0 flex-col" aria-busy={pending} aria-label="Endpoint">
      {focusHeader}
      {pending ? (
        <div className="flex-1"><GridStatus state="loading" label="Loading endpoint" /></div>
      ) : (
        <div className="px-[12px] py-[12px]">
          <EmptyState
            className="max-w-[520px]"
            title={title}
            note={<span role={error ? "alert" : undefined}>{message}</span>}
            action={
              <div className="flex gap-[6px]">
                {error && !unavailable && <button type="button" className={ACTION_CLASS} onClick={onRetry}>Try again</button>}
                <button type="button" className={ACTION_CLASS} onClick={onBack}>Back to Explore</button>
              </div>
            }
          />
        </div>
      )}
    </section>
  );
}

export default function EndpointScopeBoundary({ scopePath = [], children, focusHeader, onBack }) {
  const [attempt, setAttempt] = useState(0);
  const endpointIds = [...new Set(scopePath
    .filter((step) => step.dim === "endpoints")
    .flatMap(stepIds).map((id) => String(id).toLowerCase()))].sort();
  if (!endpointIds.length) return children;
  // Key the lookup so a previous endpoint's successful read cannot mount the
  // next endpoint's panels while useQuery starts its replacement request.
  return (
    <EndpointLookup key={`${JSON.stringify(endpointIds)}:${attempt}`} endpointIds={endpointIds}
      focusHeader={focusHeader} onBack={onBack} onRetry={() => setAttempt((value) => value + 1)}>
      {children}
    </EndpointLookup>
  );
}
